import { FiHelpCircle } from 'react-icons/fi'

import { Alert, Spinner } from '@vtex/shoreline'

import BadgeIsValid from '~/components/BadgeIsValid'
import Panel from '~/components/Panel'
import Tooltip from '~/components/Tooltip'
import { Badge } from '~/components/UI/Badge'
import { FormGroup, InputMoney, Label } from '~/components/unform'
import { formatMoney } from '~/helpers'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import LoadingValue from '~/modules/retailMedia/components/CampaignForm/elements/Investment/LoadingValue'
import { useStandardBudgetForm } from '../hooks'

/**
 * Componente de orçamento padrão para campanhas de Retail Media.
 * O usuário pode definir o orçamento diário.
 * Serão definidos dinamicamente o CPM da campanha e as reproduções diárias.
 */

const StandardForm: BudgetComponent = props => {
  const { t } = useAPPTranslation()
  const { Title, isLoading, hasToFillFields, values } = props

  const {
    handleChange,
    hasNoInventory,
    sectionIsValid,
    formFields,
    minDailyBudgetValue
  } = useStandardBudgetForm(props)

  return (
    <Panel
      data-investment-budget="standard-form"
      iconLeft={
        isLoading ? (
          <Spinner size={20} />
        ) : (
          <BadgeIsValid isValid={sectionIsValid} />
        )
      }
      title={Title}
      description={t('rm:campaign.create.Investment.description')}
    >
      {hasToFillFields && (
        <Alert variant="warning" style={{ marginBottom: '1rem' }}>
          {t('rm:campaign.form.alerts.fillAllFields')}
        </Alert>
      )}

      {hasNoInventory && (
        <Alert variant="critical" style={{ marginBottom: '1rem' }}>
          {t('rm:campaign.form.alerts.noInventory')}
        </Alert>
      )}

      <section className="d-flex gap-3">
        <div className="pe-3 border-end">
          <FormGroup>
            <Label htmlFor={formFields?.budgetDaily?.name} isRequired>
              {t('rm:dailyBudget')}
              <Tooltip
                icon={<FiHelpCircle />}
                text={t('rm:dailyBudget.tooltip')}
              />
              <LoadingValue
                width="90px"
                height="0.85rem"
                isLoading={isLoading}
                style={{ marginLeft: '0.5rem' }}
              >
                {minDailyBudgetValue > 0 ? (
                  <span style={{ marginLeft: '8px', marginRight: '8px' }}>
                    ({t('rm:minimum')}: {formatMoney(minDailyBudgetValue)})
                  </span>
                ) : null}
              </LoadingValue>
            </Label>
            <InputMoney
              id={formFields?.budgetDaily?.name}
              name={formFields?.budgetDaily?.name}
              required
              disabled={formFields?.budgetDaily?.isDisabled}
              onChange={data =>
                handleChange(data, formFields?.budgetDaily?.key)
              }
              showBadgeIsValid={formFields?.budgetDaily?.showBadgeIsValid}
              showBadgeIsInvalid={formFields?.budgetDaily?.showBadgeIsInvalid}
            />
          </FormGroup>
        </div>
        <div className="pe-3 border-end">
          <Label>
            {t('rm:cpm.title')}
            <Tooltip
              icon={<FiHelpCircle />}
              text={t('rm:metrics.cpm.tooltip')}
            />
          </Label>
          <LoadingValue
            isLoading={isLoading}
            width="4.911rem"
            height="1.625rem"
          >
            {hasToFillFields ? (
              <>
                <Badge template="warning">{t('rm:pending')}</Badge>
              </>
            ) : (
              <span>{formatMoney(values?.cpmValue)}</span>
            )}
          </LoadingValue>
        </div>
        <div className="pe-3">
          <Label>
            {t('rm:campaign.create.Investment.dailyReproductions.title')}
            <Tooltip
              icon={<FiHelpCircle />}
              text={t(
                'rm:campaign.create.Investment.dailyReproductions.tooltip'
              )}
            />
          </Label>
          <LoadingValue
            isLoading={isLoading}
            width="4.911rem"
            height="1.625rem"
          >
            {hasToFillFields || !values?.dailyReproductionsValue ? (
              <Badge template="warning">{t('rm:pending')}</Badge>
            ) : (
              <span>{values?.dailyReproductionsValue}</span>
            )}
          </LoadingValue>
        </div>
      </section>
    </Panel>
  )
}

export default StandardForm
